import { rgba } from 'polished';
import React, { useEffect, useState } from 'react';
import styled, { css } from 'styled-components';
import { Icon } from '~/components';
import { colors, weights } from '~/styles';
import { subscribe, getStatus } from '~/version.js';

const urgencyStyles = {
  warning: css`
    background-color: ${rgba(colors.warning, 0.2)};
    border-color: ${rgba(colors.warning, 0.4)};

    &:hover {
      background-color: ${rgba(colors.warning, 0.3)};
    }
  `,
  danger: css`
    background-color: ${rgba(colors.danger, 0.25)};
    border-color: ${rgba(colors.danger, 0.4)};

    &:hover {
      background-color: ${rgba(colors.danger, 0.35)};
    }
  `,
};

const Card = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.25rem;
  background-color: ${rgba(colors.white, 0.1)};
  border: solid 0.0625rem ${rgba(colors.white, 0.2)};
  border-radius: 0.375rem;
  width: 9.0625rem;
  cursor: pointer;

  &:hover {
    background-color: ${rgba(colors.white, 0.15)};
  }

  ${({ $urgency }) => $urgency && urgencyStyles[$urgency]}
`;

const CardText = styled.p`
  margin: 0;
  color: ${colors.white};
  font-size: 0.875rem;
  font-weight: ${weights.bold};
  line-height: 1.4;
`;

const HoverLabel = styled.div`
  position: absolute;
  top: 50%;
  left: calc(100% - 0.125rem);
  padding: 0.5rem;
  background-color: ${colors.white};
  border-radius: 0.25rem;
  box-shadow: 0 0 0.625rem ${rgba(colors.black, 0.25)};
  color: ${colors.black};
  font-size: 0.75rem;
  font-weight: normal;
  line-height: 0.875rem;
  width: max-content;
  max-width: 14rem;
  transform: translateY(-50%);
  pointer-events: none;
  opacity: 0;
  z-index: 95;

  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: -0.375rem;
    width: 0.375rem;
    height: 0.75rem;
    transform: translateY(-50%);
    background-color: ${colors.white};
    clip-path: polygon(100% 0, 100% 100%, 0 50%);
  }
`;

const BadgeWrapper = styled.div`
  position: relative;
  width: 100%;
  display: flex;
  justify-content: center;

  &:hover ${HoverLabel} {
    opacity: 1;
  }
`;

const Badge = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.5rem;
  height: 2.5rem;
  padding: 0;
  border: solid 0.0625rem ${rgba(colors.white, 0.2)};
  border-radius: 0.375rem;
  background-color: ${rgba(colors.white, 0.1)};
  color: ${colors.white};
  font-size: 1rem;
  cursor: pointer;

  &:hover {
    background-color: ${rgba(colors.white, 0.15)};
    color: ${colors.white};
  }

  ${({ $urgency }) => $urgency && urgencyStyles[$urgency]}
`;

const handleReload = () => window.location.reload(true);

export default function AppVersion({ collapsed }) {
  const [status, setStatus] = useState(getStatus());
  useEffect(() => subscribe((newStatus) => setStatus(newStatus)), []);
  if (!status || !['quiet', 'noisy'].includes(status)) {
    return null;
  }

  const urgency = status === 'noisy' ? 'danger' : 'warning';

  if (collapsed) {
    return (
      <BadgeWrapper>
        <Badge $urgency={urgency} onClick={handleReload}>
          <Icon icon="arrows-rotate" />
        </Badge>
        <HoverLabel>A new version of Ruddr is available. Click to update.</HoverLabel>
      </BadgeWrapper>
    );
  }

  return (
    <Card $urgency={urgency} onClick={handleReload}>
      <CardText>A new version of Ruddr is available. Click to update.</CardText>
    </Card>
  );
}
